import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { LoaderCircle, Sparkles, Star, Trophy, UserX } from 'lucide-react'
import { EVENT } from '@sdg/shared'
import { COUNTRIES, flag } from '../../components/flags'
import { celebrate, Medal } from '../Screen'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import { drawCountdownMs, drawLandsMs, drawSequenceMs } from '@sdg/shared'
import { DrawWheel, useSpinSequence } from './DrawWheel'
import type { DrawsState, DrawView } from '../admin/screenTypes'

/**
 * A lucky draw on the big screen — slides 15, 16 and 17.
 *
 * The newest winner holds the spotlight; earlier winners sit below as
 * "Previous Winners"; slots not yet drawn show "?". When a new winner
 * arrives, eligible names spin in the spotlight first (slide 17's drum-roll)
 * and then the winner locks in with a medal and confetti.
 */

const DRAW_PALETTES = {
  navy: {
    background:
      'repeating-conic-gradient(from 215deg at 50% 46%, rgba(255,255,255,0.045) 0deg 7deg, transparent 7deg 19deg), linear-gradient(135deg, #17466F 0%, #0D2D52 56%, #102440 100%)',
    glow: 'rgba(76,146,199,0.2)',
    border: '#111B2C',
    sparkle: '#F0B84D',
    sparkleAlt: '#7BC9D1',
  },
  purple: {
    background:
      'repeating-conic-gradient(from 215deg at 50% 46%, rgba(255,255,255,0.05) 0deg 7deg, transparent 7deg 19deg), linear-gradient(135deg, #5528A0 0%, #732889 54%, #461C66 100%)',
    glow: 'rgba(226,151,255,0.2)',
    border: '#27143D',
    sparkle: '#FFD16A',
    sparkleAlt: '#F0A5E3',
  },
  gold: {
    background:
      'repeating-conic-gradient(from 215deg at 50% 46%, rgba(255,255,255,0.055) 0deg 7deg, transparent 7deg 19deg), radial-gradient(ellipse at 50% 18%, rgba(230,162,60,0.2), transparent 48%), linear-gradient(135deg, #54239A 0%, #46217E 52%, #141B56 100%)',
    glow: 'rgba(255,209,106,0.2)',
    border: '#24143F',
    sparkle: '#FFD16A',
    sparkleAlt: '#F0B84D',
  },
} as const

/**
 * The draw stage itself: the bordered panel, the flags, the title and the
 * footer. Everything that makes the hall recognise the screen it is looking at.
 *
 * Extracted so the presentation cheque can be shown **on this same screen**
 * rather than on a bare stage of its own. The Committee's note was that the
 * cheque arriving should feel like the next beat of the Grand Draw, not a
 * different slide — so it keeps the frame, drops the winner's name (the cheque
 * carries it), and the cheque itself pushes up from the bottom.
 */
export function DrawShell({
  theme,
  title,
  status,
  children,
}: {
  theme: DrawView['theme']
  title: string
  status: { text: string; tone: 'amber' | 'red' }
  children: React.ReactNode
}) {
  const palette = DRAW_PALETTES[theme]

  return (
    <div className="h-full w-full min-h-0">
      <section
        className="relative isolate flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[1vmin] border-[0.45vmin] px-[2.5vmin] shadow-[0_18px_50px_rgba(11,30,63,0.22)]"
        style={{
          backgroundImage: palette.background,
          borderColor: palette.border,
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          style={{ backgroundImage: `radial-gradient(ellipse at 50% 42%, ${palette.glow}, transparent 62%)` }}
        />
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
          <Sparkles className="stage-sparkle absolute left-[9%] top-[22%] size-[1.8vmin]" style={{ color: palette.sparkle, opacity: 0.55 }} />
          <Sparkles className="stage-sparkle stage-sparkle-delayed absolute right-[14%] top-[35%] size-[1.3vmin]" style={{ color: palette.sparkleAlt, opacity: 0.55 }} />
          <Sparkles className="stage-sparkle stage-sparkle-delayed-2 absolute bottom-[18%] left-[47%] size-[1.5vmin]" style={{ color: palette.sparkle, opacity: 0.45 }} />
        </div>

        <header className="relative z-10 shrink-0 border-b border-white/15 pb-[1.2vmin] pt-[0.7vmin]">
          <div className="flex items-center justify-between gap-[2vmin]">
            <div className="stage-flags-window min-w-0 flex-1 overflow-hidden" aria-label="Flags of the 99 participating countries">
              <div className="stage-flags-marquee flex w-max" aria-hidden="true">
                {[0, 1].map((sequence) => (
                  <div key={sequence} className="flex shrink-0 items-center gap-[0.8vmin] pr-[0.8vmin]">
                    {COUNTRIES.map((code, index) => (
                      <span
                        key={`${sequence}-${index}-${code}`}
                        className="relative block size-[2.3vmin] shrink-0 overflow-hidden rounded-full border border-white/85 bg-[#E7EFF9] shadow-sm"
                      >
                        <img
                          src={flag(code, 80)}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 block size-full rounded-full object-cover"
                        />
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-[0.8vmin]">
              <span
                aria-label="Host nation: Malaysia"
                className="flex items-center gap-[0.7vmin] rounded-full border border-white/70 bg-white/10 px-[1vmin] py-[0.55vmin] text-[1.3vmin] font-bold uppercase tracking-[0.1em] text-white"
              >
                <img src={flag('my', 80)} alt="Malaysia" className="size-[2.3vmin] rounded-full object-cover ring-[0.15vmin] ring-white/80" />
                <span className="hidden sm:inline">Malaysia</span>
              </span>
              <span
                className={cn(
                  'shrink-0 rounded-full px-[1.5vmin] py-[0.55vmin] text-[1.45vmin] font-bold uppercase tracking-[0.12em]',
                  status.tone === 'red' ? 'bg-[#C0392B] text-white' : 'bg-[#E6A23C] text-[#152B46]',
                )}
              >
                {status.text}
              </span>
            </div>
          </div>
          <div className="mt-[1vmin] flex items-center justify-center gap-[1vmin]">
            <span className="h-px w-[5vmin] bg-[#E6A23C]/60" />
            <h1 className="text-[3.2vmin] font-bold uppercase tracking-[0.24em] text-[#FFD16A]">
              {title}
            </h1>
            <span className="h-px w-[5vmin] bg-[#E6A23C]/60" />
          </div>
        </header>

        {children}

        <footer className="relative z-10 flex shrink-0 justify-end border-t border-white/15 pt-[0.55vmin] text-[1.4vmin] text-white/70">
          Technology partner ·{' '}
          <a
            href={EVENT.technologyPartnerUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="ml-[0.3vmin] font-semibold text-white/75"
          >
            {EVENT.technologyPartner}
          </a>
        </footer>

      </section>

    </div>
  )
}

export function DrawScreen({ draw, pool }: { draw: DrawView; pool: DrawsState }) {
  const winners = [...draw.winners].sort((a, b) => a.position - b.position)

  /*
   * The most recently *drawn* winner — by time, not by position.
   *
   * This was `winners.at(-1)`, the highest position, which is the same thing
   * only while a draw runs forward. A re-draw replaces an *earlier* position:
   * with winners 1, 2 and 3 on the board, replacing winner 1 leaves position
   * 3 as the last in the list, so `newest` never changed, the spin was never
   * triggered, and the new name simply appeared in place of the old one with
   * no wheel and no drum-roll.
   *
   * `drawnAt` has second resolution, but the stage guard already refuses a
   * second announcement for twelve seconds, so two winners cannot share one.
   */
  const newest =
    [...winners].sort((a, b) => Date.parse(a.drawnAt) - Date.parse(b.drawnAt)).at(-1) ?? null

  /*
   * This draw's own timings. The Grand Lucky Draw counts from ten rather than
   * five, so neither the countdown nor the hand-off from wheel to card is a
   * constant any more — and the server's stage guard reads the same two
   * functions, so it cannot release the screen while this is still playing.
   */
  const countdownMs = drawCountdownMs(draw.drawNo)
  const landsMs = drawLandsMs(draw.drawNo)
  const sequenceMs = drawSequenceMs(draw.drawNo)

  /*
   * A winner who was called three times and did not come forward — slide 14.
   * They hold the spotlight while the calling happens, so the hall sees why
   * the name is about to change, and they keep their position until Admin
   * draws the replacement.
   */
  const absentee = winners.find((winner) => winner.absent) ?? null

  /*
   * The disqualification, sounded once when it happens.
   *
   * Keyed on the position rather than on a boolean so a second absence later
   * in the same draw sounds again, and so the stream re-sending the same
   * state on reconnect does not replay it. Null clears the memory, which is
   * what lets "Came forward" and then a later absence both be heard.
   */
  const sounded = useRef<number | null>(null)
  useEffect(() => {
    if (absentee == null) {
      sounded.current = null
      return
    }
    if (sounded.current === absentee.position) return
    sounded.current = absentee.position
    stageSound.disqualified()
  }, [absentee])

  /*
   * Spin only when a winner appears that this screen has not shown before —
   * not on every payload. The stream re-sends the whole state on reconnect,
   * and replaying the drum-roll for a winner announced five minutes ago would
   * look like a second draw.
   *
   * Identity, not position: a re-draw puts a *different* student in the *same*
   * position, so keying on position alone let the name swap with no drum-roll
   * at all. The student is in the key as well as the time because MySQL stores
   * whole seconds, and a replacement drawn in the same second as the absence
   * would otherwise read as the same winner.
   */
  const [spinning, setSpinning] = useState(false)
  const shown = useRef<string | null>(null)
  const mounted = useRef(false)
  const lastOpenPool = useRef<{ sample: string[]; size: number | null }>({ sample: [], size: null })

  useLayoutEffect(() => {
    if (pool.poolSample.length > 0) {
      lastOpenPool.current = {
        sample: pool.poolSample,
        size: pool.poolForOpenDraw,
      }
    }
  }, [pool.poolSample, pool.poolForOpenDraw])

  /*
   * `maskedStudentId` is still the fallback *here* — this string is never
   * shown, it only decides whether the drum-roll runs, and it has to stay
   * distinct per student even for a row with no registration code. The three
   * places the code is *displayed* show nothing when it is missing rather
   * than substituting a different identifier.
   */
  const identity = newest
    ? `${newest.position}:${newest.regNo ?? newest.maskedStudentId}:${newest.drawnAt}`
    : null

  useLayoutEffect(() => {
    if (!mounted.current) {
      mounted.current = true

      /*
       * A winner drawn a moment ago still gets its drum-roll, even though
       * this component has only just appeared.
       *
       * The screen does not show a draw until something happens in it, so
       * Admin's first "Draw winner" both *mounts* this component and delivers
       * the winner in the same payload — and the mount guard, which exists to
       * stop the spin replaying on reconnect, was swallowing it. The first
       * winner of every draw appeared with no wheel at all.
       *
       * Judged on the winner's own timestamp. The window is the length of the
       * whole sequence, so a browser clock a few seconds off the server's
       * degrades to the old behaviour (a silent first winner) rather than
       * replaying an old draw at the hall.
       */
      const age = newest ? Date.now() - Date.parse(newest.drawnAt) : Infinity

      /*
       * The lower bound is negative on purpose. MySQL rounds a `datetime` to
       * the nearest second, so a winner drawn at x.6s reads back as x+1 — in
       * the future by up to half a second, before any clock skew between the
       * server and the hall's laptop. Requiring a non-negative age threw the
       * drum-roll away at random.
       */
      if (!(age > -3000 && age < sequenceMs)) {
        shown.current = identity
        return
      }
    }

    if (identity == null || identity === shown.current) return

    /*
     * Rolled back by the cleanup unless the sequence finished.
     *
     * StrictMode runs every effect twice on mount — run, clean up, run again
     * — and without this the second run saw the first run's bookkeeping,
     * decided the winner had already been announced, and returned without
     * restarting the timer that hands the screen from the wheel to the card.
     * The wheel span and then sat there. Only the first winner after the
     * screen appeared was affected, which is why it looked intermittent.
     */
    const previous = shown.current
    shown.current = identity

    setSpinning(true)
    let finished = false

    /*
     * The fanfare goes with the *landing*, not with the card.
     *
     * The wheel stops at `landsMs` and the card takes over two seconds later
     * at `sequenceMs` — that gap is the hold, so the hall can see where the
     * pointer came to rest. Firing the fanfare at the end of the sequence put
     * silence in exactly that gap and then started the music after the moment
     * it was meant to announce. It now begins the instant the wheel stops and
     * is still playing when the card arrives.
     */
    const fanfare = window.setTimeout(() => stageSound.winner(), landsMs)

    const timer = window.setTimeout(() => {
      finished = true
      setSpinning(false)
      celebrate(draw.theme)
    }, sequenceMs)

    return () => {
      window.clearTimeout(fanfare)
      window.clearTimeout(timer)
      if (!finished) shown.current = previous
    }
  }, [identity, draw.theme])

  /*
   * The pool, held across the moment the draw closes.
   *
   * `poolSample` and `poolForOpenDraw` both empty out as soon as the draw is
   * no longer open, and the last spin of a draw runs at exactly that moment —
   * without the carried copy the final winner rolled against an empty list
   * and the count under the wheel blanked.
   *
   * The names roll through the wheel's window rather than sitting on its rim.
   * Fixed names on the rim read as "these twelve are in the running" when a
   * thousand students are; a roll shows everybody going past.
   */
  const spinNames = pool.poolSample.length > 0 ? pool.poolSample : lastOpenPool.current.sample
  const spinPoolSize = pool.poolForOpenDraw ?? lastOpenPool.current.size
  const winnerFirstName = newest?.name.trim().split(/\s+/)[0] ?? ''
  const { phase, count } = useSpinSequence(spinning, countdownMs)

  return (
    <DrawShell
      theme={draw.theme}
      title={draw.name}
      status={{
        tone: absentee ? 'red' : 'amber',
        text: absentee
          ? `Winner ${absentee.position} · not present`
          : draw.status === 'complete'
            ? `Draw complete · ${draw.winners.length} of ${draw.winnersTarget}`
            : `Winner ${newest?.position ?? Math.max(draw.winners.length, 1)} of ${draw.winnersTarget}`,
      }}
    >
        <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center py-[1.6vmin]">
          {draw.winnersTarget === 1 ? (
            <div className="flex max-w-[82vw] flex-col items-center text-center">
              {absentee ? (
                <>
                  <UserX className="size-[10vmin] text-[#F3A49B]" strokeWidth={1.5} />
                  <span className="mt-[1.4vmin] rounded-full bg-[#C0392B] px-[3vmin] py-[0.9vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.16em] text-white">
                    Not present
                  </span>
                  <h2 className="mt-[2vmin] max-w-[84vw] text-[6.4vmin] font-extrabold leading-tight text-white/55 line-through decoration-[#F3A49B]/80 decoration-[0.35vmin]">
                    {absentee.name}
                  </h2>
                  <p className="mt-[1.4vmin] text-[2.4vmin] text-white/75">
                    Called three times · a replacement will be drawn
                  </p>
                </>
              ) : (
                <>
              {!spinning && (
                <Trophy className="size-[11vmin] fill-[#F9BB45] text-[#FFD66B] drop-shadow-[0_6px_22px_rgba(255,194,64,0.3)]" strokeWidth={1.5} />
              )}
              {spinning ? (
                <>
                  <span className="mb-[3.4vmin] rounded-full bg-[#F2AE37] px-[3vmin] py-[0.9vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.16em] text-[#1C2458]">
                    Grand draw · selecting winner
                  </span>
                  <DrawWheel
                    names={spinNames}
                    winner={winnerFirstName}
                    theme={draw.theme}
                    label="Drawing the grand winner"
                    phase={phase}
                    count={count}
                    countdownMs={countdownMs}
                  />
                </>
              ) : newest ? (
                <div key={identity ?? 'grand'} className="reveal-pop flex flex-col items-center">
                  <span className="mt-[1vmin] inline-block rounded-full bg-[#F2AE37] px-[3vmin] py-[0.9vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.16em] text-[#1C2458] shadow-[0_5px_20px_rgba(248,184,55,0.28)]">
                    ★ Grand winner ★
                  </span>
                  <h2 className="mt-[2vmin] max-w-[84vw] text-[7.4vmin] font-extrabold leading-tight text-white">
                    {newest.name}
                  </h2>
                  {/*
                    Bounded and clipped, like the two winner cards below it.
                    This one was neither: a long school name wrapped onto
                    three lines on the projector and pushed the registration
                    code off the card. The cap on what can be typed is the
                    real fix; this is the belt to its braces.
                  */}
                  <p className="mx-auto mt-[1vmin] max-w-[84vw] truncate text-[2.2vmin] text-white/80">
                    {newest.school}
                    {newest.regNo && (
                      <>
                        <span className="mx-[1vmin] text-white/45">·</span>
                        <span className="font-mono tabular-nums">{newest.regNo}</span>
                      </>
                    )}
                  </p>
                  {draw.status === 'complete' && (
                    <p className="mt-[2vmin] text-[3.4vmin] font-bold italic text-[#FFD16A]">
                      Congratulations!
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <span className="mt-[1vmin] rounded-full bg-[#F2AE37] px-[3vmin] py-[0.9vmin] text-[1.9vmin] font-extrabold uppercase tracking-[0.16em] text-[#1C2458]">
                    Grand lucky draw
                  </span>
                  <p className="mt-[2vmin] text-[3vmin] font-semibold text-white">
                    Entries are closed · waiting for the grand winner
                  </p>
                  <p className="mt-[1vmin] text-[1.9vmin] text-white/70">
                    {pool.poolForOpenDraw?.toLocaleString()} students in the draw
                  </p>
                </>
              )}
                </>
              )}
            </div>
          ) : absentee ? (
            <SpotlightCard>
              <div className="absolute -right-[0.3vmin] -top-[1.5vmin] rounded-full bg-[#C0392B] px-[2.4vmin] py-[0.8vmin] text-[1.5vmin] font-bold uppercase tracking-[0.14em] text-white shadow-md">
                Not present
              </div>
              <div className="flex w-full items-center gap-[2.5vmin] px-[2vmin]">
                <span className="grid size-[9vmin] shrink-0 place-items-center rounded-full border-[0.35vmin] border-dashed border-[#E0A9A1] bg-[#FCEFEC] text-[#B03A2E]">
                  <UserX className="size-[4.5vmin]" strokeWidth={1.6} />
                </span>
                <div className="min-w-0 flex-1 text-left">
                  <p className="mb-[0.8vmin] text-[1.35vmin] font-bold uppercase tracking-[0.2em] text-[#B03A2E]">
                    Winner {absentee.position} · called three times
                  </p>
                  <h2 className="truncate text-[4.3vmin] font-bold leading-tight text-slate-400 line-through decoration-[#E0A9A1] decoration-[0.3vmin]">
                    {absentee.name}
                  </h2>
                  <p className="mt-[0.7vmin] text-[2.1vmin] text-slate-600">
                    Not present · drawing a replacement for winner {absentee.position}
                  </p>
                </div>
              </div>
            </SpotlightCard>
          ) : spinning ? (
            /*
             * The wheel sits on the stage itself, not inside the white
             * spotlight card. A card around it cropped it on a 16:9 projector
             * and left the names too small to read from the back.
             */
            <div className="flex flex-col items-center text-center">
              <p className="mb-[3.4vmin] rounded-full bg-[#F0AD37] px-[2.8vmin] py-[0.8vmin] text-[1.7vmin] font-extrabold uppercase tracking-[0.18em] text-[#17365D]">
                Drawing · winner {Math.max(draw.winners.length, 1)} of {draw.winnersTarget}
              </p>
              <DrawWheel
                names={spinNames}
                winner={winnerFirstName}
                theme={draw.theme}
                label={`Drawing winner ${Math.max(draw.winners.length, 1)}`}
                phase={phase}
                count={count}
                countdownMs={countdownMs}
              />
            </div>
          ) : newest ? (
            <SpotlightCard key={identity ?? 'winner'} className="reveal-pop">
              <div className="absolute -right-[0.3vmin] -top-[1.5vmin] rounded-full bg-[#F0AD37] px-[2.4vmin] py-[0.8vmin] text-[1.5vmin] font-bold uppercase tracking-[0.14em] text-[#17365D] shadow-md">
                {draw.status === 'complete'
                  ? 'Draw complete'
                  : `Winner ${newest.position} of ${draw.winnersTarget}`}
              </div>
              <div className="flex w-full items-center gap-[2.5vmin] px-[2vmin]">
                <Medal
                  n={newest.position}
                  tone={draw.theme === 'gold' ? 'gold' : draw.theme === 'purple' ? 'purple' : 'navy'}
                  size="9vmin"
                />
                <div className="min-w-0 flex-1 text-left">
                  <p className="mb-[0.8vmin] flex items-center gap-[0.8vmin] text-[1.7vmin] font-bold uppercase tracking-[0.2em] text-[#A56A19]">
                    <Star className="size-[1.2vmin] fill-current" /> Winner {newest.position}
                  </p>
                  <h2 className="truncate text-[5.2vmin] font-bold leading-tight text-[#102B4C]">
                    {newest.name}
                  </h2>
                  <p className="mt-[0.7vmin] truncate text-[2.1vmin] text-slate-600">
                    {newest.school}
                    {newest.regNo && (
                      <>
                        <span className="mx-[0.8vmin] text-slate-300">·</span>
                        <span className="font-mono tabular-nums">{newest.regNo}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              {draw.winners.length === draw.winnersTarget && (
                <p className="mt-[1.1vmin] text-[1.9vmin] font-bold uppercase tracking-[0.14em] text-[#A56A19]">
                  Congratulations
                </p>
              )}
            </SpotlightCard>
          ) : (
            <SpotlightCard>
              <Sparkles className="mx-auto size-[3vmin] text-[#D39B35]" />
              <p className="mt-[0.8vmin] text-[2.8vmin] font-bold text-[#102B4C]">Entries are closed</p>
              <p className="mt-[0.7vmin] text-[1.7vmin] text-slate-500">
                {pool.poolForOpenDraw?.toLocaleString()} students entered · waiting for the first draw
              </p>
            </SpotlightCard>
          )}
        </div>

        {draw.winnersTarget > 1 && (
          <div className="relative z-10 shrink-0 pb-[0.8vmin]">
            <p className="mb-[0.8vmin] text-[1.35vmin] font-bold uppercase tracking-[0.2em] text-white/65">
              Winner positions
            </p>
            <div
              className="grid gap-[0.8vmin]"
              style={{ gridTemplateColumns: `repeat(${draw.winnersTarget}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: draw.winnersTarget }, (_, index) => index + 1)
                .map((slot) => (
                  <PreviousWinner
                    key={slot}
                    position={slot}
                    winner={spinning && slot === newest?.position
                      ? undefined
                      : winners.find((winner) => winner.position === slot)}
                    isDrawing={spinning && slot === newest?.position}
                    onStage={!spinning && !absentee && slot === newest?.position}
                    theme={draw.theme}
                  />
                ))}
            </div>
          </div>
        )}

    </DrawShell>
  )
}
function SpotlightCard({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'relative flex min-h-[14vmin] w-[78%] flex-col items-center justify-center rounded-[1.5vmin] border-[0.35vmin] border-[#E6A23C] bg-white px-[2vmin] py-[2vmin] text-center shadow-[0_10px_24px_rgba(8,22,43,0.3)]',
        className,
      )}
    >
      {children}
    </div>
  )
}

function PreviousWinner({
  position,
  winner,
  isDrawing,
  onStage,
  theme,
}: {
  position: number
  winner: DrawView['winners'][number] | undefined
  isDrawing: boolean
  onStage: boolean
  theme: DrawView['theme']
}) {
  const absent = winner?.absent === true

  return (
    <div className={cn(
      'flex min-h-[9.5vmin] min-w-0 items-center gap-[0.8vmin] rounded-[1vmin] border px-[1vmin] py-[0.8vmin]',
      absent
        ? 'border-[#C0392B] bg-[#FCEFEC] text-[#7E2B20]'
        : onStage
          ? 'border-[#F0B84D] bg-[#FFF3D5] text-[#102B4C] shadow-[0_0_16px_rgba(240,184,77,0.25)]'
          : winner
            ? 'border-white/70 bg-white/95 text-[#102B4C]'
            : isDrawing
              ? 'border-[#F0B84D]/70 bg-white/15 text-white'
              : 'border-dashed border-white/35 bg-white/10 text-white/70',
    )}>
      {absent ? (
        <span className="grid size-[4.2vmin] shrink-0 place-items-center rounded-full border-[0.2vmin] border-dashed border-[#E0A9A1] bg-white text-[#B03A2E]">
          <UserX className="size-[2.2vmin]" strokeWidth={1.8} />
        </span>
      ) : winner ? (
        <Medal
          n={winner.position}
          tone={theme === 'purple' ? 'purple' : 'navy'}
          size="4.2vmin"
        />
      ) : isDrawing ? (
        <span className="grid size-[4.2vmin] shrink-0 place-items-center rounded-full bg-[#F0B84D] text-[#173B66]">
          <LoaderCircle className="size-[2.3vmin] animate-spin" />
        </span>
      ) : (
        <span className="grid size-[4.2vmin] shrink-0 place-items-center rounded-full bg-white/90 text-[2.3vmin] font-bold text-[#607B98]">
          ?
        </span>
      )}
      <div className="min-w-0">
        <p className={cn(
          'text-[1.25vmin] font-bold uppercase tracking-[0.16em]',
          absent
            ? 'text-[#B03A2E]'
            : onStage || isDrawing ? 'text-[#F0B84D]' : winner ? 'text-[#118F91]' : 'text-white/65',
        )}>
          {absent
            ? 'Not present'
            : isDrawing ? 'Drawing now' : onStage ? 'On stage' : `Winner ${position}`}
        </p>
        {winner ? (
          <>
            <p className={cn(
              'truncate text-[1.85vmin] font-bold',
              absent ? 'text-slate-400 line-through decoration-[#E0A9A1]' : 'text-[#102B4C]',
            )}>
              {winner.name}
            </p>
            {absent ? (
              <p className="truncate text-[1.3vmin] italic text-[#B03A2E]">Re-draw pending</p>
            ) : (
              <>
                <p className="truncate text-[1.3vmin] text-slate-500">{winner.school}</p>
                {winner.regNo && (
                  <p className="truncate font-mono text-[1.15vmin] text-slate-400">{winner.regNo}</p>
                )}
              </>
            )}
          </>
        ) : isDrawing ? (
          <p className="mt-[1.5vmin] text-[1.3vmin] italic text-white/80">Revealing winner</p>
        ) : (
          <p className="mt-[1.5vmin] text-[1.3vmin] italic text-white/75">next draw</p>
        )}
      </div>
    </div>
  )
}

